import { useContext, useMemo, useState } from 'react';
import * as React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Button, IconButton, keyframes, useMediaQuery, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  AppConstantsContext,
  NavigationContext,
  TranscriptionStoreContext,
  UserContext,
  View,
} from '../../../context';
import { TranscriptionState } from '../../../entities';
import useLocaleNavigate from '../../../hooks/useLocalNavigation';
import { TrackingAction, TrackingCategory, trackingService } from '../../../services';
import { AUTH_URL } from '../../../utils';
import SheetDownloadButton from '../../molecules/DownloadMenu/SheetDownloadButton';
import PricingTableModal from '../../templates/PricingTableModal';
import InputContainerModal from '../InputContainer/InputContainerModal';
import useSaleEvent from '../Pricing/useSaleEvent';
import UserMenu from '../UserMenu/UserMenu';

export interface NavigationHeaderProps {
  shortIcon?: boolean;
}

const fadeInNav = keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateX(-180px)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateX(0)',
  },
});

const useStyles = makeStyles<{ additional?: { [key: string]: any } }>()((theme, props) => ({
  container: {
    width: '100%',
    background: '#2d2d2d',
    zIndex: 100,
    position: 'sticky',
    top: 0,
    margin: '0',
  },
  stickyBar: {
    maxWidth: '1300px',
    margin: 'auto',
    borderBottom: '1px solid #ffffff63',
    padding: '0',
  },
  wrapper: {
    display: 'flex',
    paddingTop: '4px',
    alignItems: 'center',
    margin: '0 0.5rem 0 1rem',
  },
  menuButtons: {
    display: 'flex',
    gap: '1em',
    opacity: 0,
    transform: 'translateX(-19px)',
    '&.fadeIn': {
      animation: `${fadeInNav} 0.5s ease-out forwards`,
    },

    [theme.breakpoints.down('lg')]: {
      margin: '0',
    },
  },
  menuButtonsRight: {
    display: 'flex',
    gap: '.3em',
    margin: '0 0',
    justifyContent: 'space-between',
    [theme.breakpoints.down('lg')]: {
      margin: '0',
    },
  },
  button: {
    fontWeight: '500',
    color: '#fff',
    [theme.breakpoints.down('lg')]: {
      fontSize: '12px',
    },
  },
  upgrade: {
    fontWeight: '500',
    color: '#fff',
    [theme.breakpoints.down('lg')]: {
      fontSize: '12px',
    },
    ...props.additional?.navigationButton,
  },
  area: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '1300px',
  },
  logo: {
    marginRight: '1em',
  },
  upgradeButtonWrapper: {
    height: 40,
    marginRight: '1em',
  },
  btnSpan: {
    cursor: 'pointer',
  },
}));

const NavigationHeader: React.FC<NavigationHeaderProps> = () => {
  const theme = useTheme();
  const { isMusehub, musehubPurchaseUrl, jobType } = useContext(AppConstantsContext);
  const { saleEvent } = useSaleEvent();
  const { selectedTranscription } = useContext(TranscriptionStoreContext);

  const { t, i18n } = useTranslation();

  const locale = i18n.language;
  const { currentView, setCurrentView, setCurrentSubMenu } = useContext(NavigationContext);
  const { classes } = useStyles({ additional: saleEvent?.styles });
  const { user, isLoadingUserCompleted } = useContext(UserContext);
  const [openPricingModal, setOpenPricingModal] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { wpLandingPage } = useContext(AppConstantsContext);
  const { navigateTo, localeUrl } = useLocaleNavigate();
  const [openInputContainer, setOpenInputContainer] = useState(false);
  const isFileMode = useMemo(
    () => currentView === View.display || currentView === View.editor,
    [currentView],
  );
  const noUpgrade = isFileMode && selectedTranscription?.state === TranscriptionState.Error;

  const handleClick = (view: View) => {
    setCurrentView(view);
    setCurrentSubMenu(null);
  };

  const navigateToRegister = () => {
    if (isMusehub) return;
    trackingService.event(TrackingCategory.Account, TrackingAction.CLICK_REGISTER);
    window.location.href = `${AUTH_URL}${locale}/register?redirect_url=${window.location.href}`;
  };

  const navigateToLogin = () => {
    if (isMusehub) return;
    trackingService.event(TrackingCategory.Account, TrackingAction.CLICK_LOGIN);
    window.location.href = `${AUTH_URL}${locale}/login?redirect_url=${window.location.href}`;
  };

  const handleShowPricingTable = () => {
    if (isMusehub) {
      window.open(musehubPurchaseUrl, '_blank');
    } else {
      setOpenPricingModal(true);
    }
  };

  const iconHref = useMemo(() => {
    if (isMusehub) return localeUrl('my-files');
    if (user && i18n.language) return localeUrl('my-files');
    return wpLandingPage;
  }, [isMusehub, user, wpLandingPage, i18n.language]);

  if (isLoadingUserCompleted) {
    return (
      <div className={classes.container}>
        <div className={classes.stickyBar}>
          <div className={classes.wrapper}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
              {isFileMode ? (
                <>
                  <IconButton
                    onClick={() => navigateTo('/my-files')}
                    size="large"
                    style={{ padding: '0' }}
                  >
                    <ArrowBackIcon sx={{ fontSize: '24px' }} />
                  </IconButton>
                  <img
                    className={classes.logo}
                    role="presentation"
                    src={`/img/${jobType}/icon_logo.png`}
                    height="30"
                    width="30"
                    alt="logo"
                    id="btn_nav_home"
                  />
                </>
              ) : (
                <a className={classes.btnSpan} href={iconHref as string}>
                  <img
                    className={classes.logo}
                    role="presentation"
                    src={`/img/${jobType}/logo.png`}
                    height="30"
                    width="137.13"
                    alt="logo"
                    id="btn_nav_home"
                  />
                </a>
              )}
            </div>
            {/* </a> */}

            <div className={classes.area}>
              <div className={`${classes.menuButtons} ${isFileMode && 'fadeIn'}`}>
                {!isMobile && isFileMode && (
                  <>
                    <Button
                      onClick={() => handleClick(View.display)}
                      className={classes.button}
                      id="btn_nav_display"
                      color={currentView === View.display ? 'primary' : 'inherit'}
                      style={{
                        color: currentView === View.display ? theme.palette.primary.main : '#fff',
                      }}
                    >
                      {t('nav.display')}
                    </Button>
                    <Button
                      onClick={() => handleClick(View.editor)}
                      className={classes.button}
                      id="btn_nav_editor"
                      style={{
                        color: currentView === View.editor ? theme.palette.primary.main : '#fff',
                      }}
                    >
                      {t('nav.note_editor')}
                    </Button>
                  </>
                )}
                {isFileMode
                  && selectedTranscription
                  && selectedTranscription?.state !== TranscriptionState.Error && (
                    <SheetDownloadButton
                      transcription={selectedTranscription}
                      showPricingTable={handleShowPricingTable}
                      onUnlockTranscriptionFinish={(jobId) => {
                        window.location.href = localeUrl(`/file?id=${jobId}`);
                      }}
                    />
                )}
              </div>

              <div className={classes.menuButtonsRight}>
                {user && !isMobile ? (
                  <div>
                    {user.subscriptions?.length === 0 && !noUpgrade && (
                      <div className={classes.upgradeButtonWrapper}>
                        <Button
                          onClick={handleShowPricingTable}
                          className={classes.upgrade}
                          id="btn_nav_upgrade"
                          variant="contained"
                        >
                          {saleEvent?.navigationText ? `${saleEvent.navigationText}` : 'Upgrade'}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div />
                )}
                {!user && !isMobile ? (
                  <>
                    <Button
                      id="btn_nav_login"
                      aria-label="login"
                      className={classes.button}
                      onClick={navigateToLogin}
                      style={{ color: '#fff' }}
                    >
                      {t('nav.login')}
                    </Button>
                    <Button
                      id="btn_nav_register"
                      aria-label="register"
                      style={{ color: '#fff', marginRight: '.5em' }}
                      className={classes.button}
                      onClick={navigateToRegister}
                    >
                      {t('nav.sign_up')}
                    </Button>
                  </>
                ) : (
                  <div />
                )}

                <UserMenu
                  transcription={selectedTranscription}
                  navigateToLogin={navigateToLogin}
                  navigateToRegister={navigateToRegister}
                />
              </div>
            </div>
          </div>

          {openPricingModal && (
            <PricingTableModal
              transcription={isFileMode && selectedTranscription}
              handleClose={() => setOpenPricingModal(false)}
              open={openPricingModal}
            />
          )}
          {openInputContainer && (
            <InputContainerModal handleClose={() => setOpenInputContainer(false)} />
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default NavigationHeader;
